<script>
  import { onMount } from 'svelte';
  import Layout from '../components/Layout.svelte';
  import ComplianceReportViewer from '../components/ComplianceReportViewer.svelte';
  import { complianceReportsApi, customersApi } from '../stores/api.js';
  import { overallColors, fallbackBadge } from '../lib/complianceMaps.js';
  import { isAdmin, isCustomer } from '../stores/roles.js';
  import { _ } from '../lib/i18n.js';
  import {
    ShieldCheck, Eye, Loader, AlertCircle, Filter, Calendar
  } from 'lucide-svelte';

  let loading = true;
  let message = null;
  let reports = [];
  let customers = [];

  // Admin filters
  let filterCustomerId = '';
  let filterFrom = '';
  let filterTo = '';

  // Viewer state
  let viewingReport = null;
  let loadingReport = false;

  onMount(() => {
    loadReports();
    if ($isAdmin) loadCustomers();
  });

  async function loadReports() {
    loading = true;
    try {
      const params = {};
      if (!$isCustomer) {
        if (filterCustomerId) params.customerId = filterCustomerId;
        if (filterFrom) params.from = filterFrom;
        if (filterTo) params.to = filterTo;
      }
      const response = await complianceReportsApi.list(params);
      reports = response.reports || [];
    } catch (err) {
      console.error('[ComplianceReports] Error loading reports:', err);
      message = { type: 'error', text: err.message };
    } finally {
      loading = false;
    }
  }

  async function loadCustomers() {
    try {
      const response = await customersApi.getAll();
      customers = response.customers || [];
    } catch (err) {
      console.error('[ComplianceReports] Error loading customers:', err);
    }
  }

  function applyFilters() {
    loadReports();
  }

  async function viewReport(id) {
    loadingReport = true;
    try {
      viewingReport = await complianceReportsApi.get(id);
    } catch (err) {
      console.error('[ComplianceReports] Error loading report:', err);
      message = { type: 'error', text: err.message };
    } finally {
      loadingReport = false;
    }
  }

  function closeViewer() {
    viewingReport = null;
  }

  function pretty(value) {
    return String(value ?? '').replace(/[-_]/g, ' ');
  }
  function formatDate(value) {
    if (!value) return '—';
    return new Date(value).toLocaleDateString('it-IT', { year: 'numeric', month: 'short', day: 'numeric' });
  }
</script>

<Layout>
  <div class="space-y-6">
    <!-- Header -->
    <div class="flex items-center space-x-4">
      <div class="p-3 bg-linear-to-br from-primary-500 to-primary-600 rounded-2xl shadow-lg">
        <ShieldCheck size={28} class="text-white" />
      </div>
      <div>
        <h1 class="text-2xl font-bold text-gray-900 dark:text-white">{$_('complianceReports.title')}</h1>
        <p class="text-sm text-gray-500 dark:text-gray-400">{$_('complianceReports.subtitle')}</p>
      </div>
    </div>

    {#if message}
      <div class="p-4 rounded-lg flex items-center space-x-2 bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-800">
        <AlertCircle size={20} />
        <span>{message.text}</span>
        <button class="ml-auto hover:opacity-70" onclick={() => message = null}>&times;</button>
      </div>
    {/if}

    <!-- Admin filters -->
    {#if $isAdmin}
      <div class="card p-4 flex flex-wrap items-end gap-4">
        <div class="flex items-center space-x-2">
          <Filter size={18} class="text-gray-500" />
          <select class="input" bind:value={filterCustomerId} onchange={applyFilters}>
            <option value="">{$_('complianceReports.allCustomers')}</option>
            {#each customers as customer}
              <option value={customer.id}>{customer.companyName || `${customer.name} ${customer.surname}`}</option>
            {/each}
          </select>
        </div>
        <div>
          <label class="label">{$_('complianceReports.from')}</label>
          <input type="date" class="input" bind:value={filterFrom} onchange={applyFilters} />
        </div>
        <div>
          <label class="label">{$_('complianceReports.to')}</label>
          <input type="date" class="input" bind:value={filterTo} onchange={applyFilters} />
        </div>
      </div>
    {/if}

    <!-- List -->
    {#if loading}
      <div class="card p-12 text-center">
        <Loader size={48} class="mx-auto text-primary-500 animate-spin mb-4" />
        <p class="text-gray-500 dark:text-gray-400">{$_('common.loading')}</p>
      </div>
    {:else if reports.length === 0}
      <div class="card p-12 text-center">
        <ShieldCheck size={48} class="mx-auto text-gray-300 dark:text-gray-600 mb-4" />
        <p class="text-gray-500 dark:text-gray-400">{$_('complianceReports.noReports')}</p>
      </div>
    {:else}
      <div class="card overflow-hidden">
        <div class="overflow-x-auto">
          <table class="w-full">
            <thead class="bg-gray-50 dark:bg-gray-800/50">
              <tr>
                <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{$_('complianceReports.reportId')}</th>
                {#if $isAdmin}
                  <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{$_('complianceReports.customer')}</th>
                {/if}
                <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{$_('complianceReports.period')}</th>
                <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{$_('complianceReports.status')}</th>
                <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{$_('complianceReports.riskScore')}</th>
                <th class="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{$_('common.actions')}</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-200 dark:divide-gray-700">
              {#each reports as report (report.id)}
                <tr class="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                  <td class="px-4 py-4 font-medium text-gray-900 dark:text-white">{report.reportId}</td>
                  {#if $isAdmin}
                    <td class="px-4 py-4 text-sm text-gray-600 dark:text-gray-300">{report.customerName || report.customerId}</td>
                  {/if}
                  <td class="px-4 py-4 text-sm text-gray-500 dark:text-gray-400">
                    <div class="flex items-center space-x-1">
                      <Calendar size={14} />
                      <span>{formatDate(report.periodStart)} → {formatDate(report.periodEnd)}</span>
                    </div>
                  </td>
                  <td class="px-4 py-4">
                    <span class="px-2.5 py-1 text-xs font-bold border rounded {overallColors[report.overallStatus] || fallbackBadge}">
                      {pretty(report.overallStatus)}
                    </span>
                  </td>
                  <td class="px-4 py-4 text-sm text-gray-600 dark:text-gray-300">
                    {report.riskScore ?? '—'}{report.riskScore != null ? '/100' : ''}
                  </td>
                  <td class="px-4 py-4 text-right">
                    <button
                      class="p-2 text-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/30 rounded-lg transition-colors"
                      onclick={() => viewReport(report.id)}
                      title={$_('common.view')}
                      disabled={loadingReport}
                    >
                      {#if loadingReport}
                        <Loader size={18} class="animate-spin" />
                      {:else}
                        <Eye size={18} />
                      {/if}
                    </button>
                  </td>
                </tr>
              {/each}
            </tbody>
          </table>
        </div>
      </div>
    {/if}
  </div>

  <ComplianceReportViewer report={viewingReport} onClose={closeViewer} />
</Layout>
